export interface TechDetailItem {
  name: string;
  logo?: string;
  icon?: string;
  href?: string;
  featured?: boolean;
}

export interface TechCategory {
  id: string;
  title: string;
  description: string;
  icon: string;
  isAi?: boolean;
  items: TechDetailItem[];
}

export const technologiesPageData = {
  hero: {
    badge: "Our Technology Stack",
    headline: "Modern Technology. Proven Engineering.",
    subtext:
      "We combine proven frameworks, modern cloud infrastructure and AI capabilities to build secure, scalable digital products.",
    primaryCTA: "Start Your Project",
    secondaryCTA: "Talk to a Laravel Expert",
  },
  supportPoints: [
    { title: "Performance", desc: "Optimized code, caching and infrastructure for fast, reliable products.", icon: "ri-speed-line" },
    { title: "Security", desc: "Best-practice security baked into every layer of the stack.", icon: "ri-shield-check-line" },
    { title: "Scalability", desc: "Architecture designed to grow seamlessly with your users.", icon: "ri-expand-diagonal-line" },
    { title: "Maintainability", desc: "Clean, documented, long-term maintainable codebases.", icon: "ri-tools-line" },
  ],
  categories: [
    {
      id: "backend",
      title: "Backend",
      description: "Secure, scalable server-side foundations for modern applications.",
      icon: "ri-server-line",
      items: [
        { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg", featured: true, href: "/technologies/laravel" },
        { name: "PHP", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/php/php-original.svg", href: "/technologies/php" },
        { name: "Node.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nodejs/nodejs-original.svg", href: "/technologies/node-js" },
      ],
    },
    {
      id: "frontend",
      title: "Frontend",
      description: "Fast, responsive and accessible user interfaces.",
      icon: "ri-window-line",
      items: [
        { name: "Vue.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/vuejs/vuejs-original.svg", href: "/technologies/vue-js" },
        { name: "React", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg", href: "/technologies/react" },
        { name: "JavaScript", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/javascript/javascript-original.svg" },
        { name: "Tailwind CSS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tailwindcss/tailwindcss-original.svg" },
        { name: "HTML5", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/html5/html5-original.svg" },
        { name: "CSS3", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/css3/css3-original.svg" },
      ],
    },
    {
      id: "mobile",
      title: "Mobile",
      description: "Cross-platform mobile apps from a single codebase.",
      icon: "ri-smartphone-line",
      items: [
        { name: "Flutter", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/flutter/flutter-original.svg", href: "/technologies/flutter" },
        { name: "Dart", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/dart/dart-original.svg" },
      ],
    },
    {
      id: "database",
      title: "Database",
      description: "Reliable, high-performance data storage and caching.",
      icon: "ri-database-2-line",
      items: [
        { name: "MySQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-original.svg" },
        { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg" },
        { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg" },
      ],
    },
    {
      id: "devops",
      title: "DevOps",
      description: "Automated pipelines and reliable delivery workflows.",
      icon: "ri-settings-3-line",
      items: [
        { name: "Docker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg" },
        { name: "Git", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/git/git-original.svg" },
        { name: "GitHub", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/github/github-original.svg" },
        { name: "CI/CD", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/githubactions/githubactions-original.svg" },
      ],
    },
    {
      id: "cloud",
      title: "Cloud",
      description: "Scalable, secure cloud infrastructure and CDN.",
      icon: "ri-cloud-line",
      items: [
        { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg", href: "/technologies/aws" },
        { name: "Cloudflare", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/cloudflare/cloudflare-original.svg" },
        { name: "DigitalOcean", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/digitalocean/digitalocean-original.svg" },
      ],
    },
    {
      id: "tools",
      title: "Tools & Others",
      description: "The everyday toolkit behind productive engineering.",
      icon: "ri-tools-line",
      items: [
        { name: "Postman", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postman/postman-original.svg" },
        { name: "Figma", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/figma/figma-original.svg" },
        { name: "Nginx", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nginx/nginx-original.svg" },
        { name: "Jira", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/jira/jira-original.svg" },
        { name: "Slack", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/slack/slack-original.svg" },
        { name: "Trello", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/trello/trello-original.svg" },
      ],
    },
    {
      id: "ai",
      title: "AI & AI Development",
      description: "Intelligent capabilities integrated into real products.",
      icon: "ri-bard-line",
      isAi: true,
      items: [
        { name: "ChatGPT", logo: "https://registry.npmmirror.com/@lobehub/icons-static-svg/latest/files/icons/openai.svg", href: "/technologies/ai-development" },
        { name: "Claude", logo: "https://registry.npmmirror.com/@lobehub/icons-static-svg/latest/files/icons/claude-color.svg", href: "/technologies/ai-development" },
        { name: "Claude Code", logo: "https://registry.npmmirror.com/@lobehub/icons-static-svg/latest/files/icons/claude-color.svg", href: "/technologies/ai-development" },
        { name: "GitHub Copilot", logo: "https://registry.npmmirror.com/@lobehub/icons-static-svg/latest/files/icons/githubcopilot.svg", href: "/technologies/ai-development" },
        { name: "Gemini", logo: "https://registry.npmmirror.com/@lobehub/icons-static-svg/latest/files/icons/gemini-color.svg", href: "/technologies/ai-development" },
        { name: "Cursor", logo: "https://www.cursor.com/assets/images/logo.svg", href: "/technologies/ai-development" },
        { name: "OpenAI Codex", logo: "https://registry.npmmirror.com/@lobehub/icons-static-svg/latest/files/icons/codex-color.svg", href: "/technologies/ai-development" },
      ],
    },
  ],
  laravelFeature: {
    badge: "Primary Expertise",
    title: "Laravel — Our Core Expertise",
    text: "Laravel is at the heart of our backend engineering capabilities. We use Laravel to build secure APIs, SaaS platforms, enterprise applications and high-performance digital products.",
    items: [
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "PHP", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/php/php-original.svg" },
      { name: "MySQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-original.svg" },
      { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg" },
      { name: "REST API", icon: "ri-exchange-line" },
      { name: "Vue.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/vuejs/vuejs-original.svg" },
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
      { name: "Docker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg" },
    ],
    cta: "Explore Laravel Development",
    href: "/services/laravel-development",
  },
  aiFeature: {
    badge: "AI-Ready Engineering",
    title: "AI-Ready Software Engineering",
    text: "Bring intelligent capabilities into your applications, SaaS platforms and business workflows through modern AI integrations.",
    items: [
      { name: "ChatGPT", logo: "https://registry.npmmirror.com/@lobehub/icons-static-svg/latest/files/icons/openai.svg" },
      { name: "Claude", logo: "https://registry.npmmirror.com/@lobehub/icons-static-svg/latest/files/icons/claude-color.svg" },
      { name: "Gemini", logo: "https://registry.npmmirror.com/@lobehub/icons-static-svg/latest/files/icons/gemini-color.svg" },
      { name: "OpenAI", logo: "https://registry.npmmirror.com/@lobehub/icons-static-svg/latest/files/icons/openai.svg" },
      { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
      { name: "REST API", icon: "ri-exchange-line" },
      { name: "Cloud", icon: "ri-cloud-line" },
    ],
    cta: "Explore AI Development",
    href: "/services/ai-development",
  },
};